import { LICENSE_CATEGORY_LABELS } from '@utils/constants';

export interface MockLicense {
  id: number;
  applicant_id: number;
  category: 'PILOT' | 'CABIN_CREW' | 'FLIGHT_DISPATCHER' | 'AME' | 'ATC' | 'ATSEP' | 'ASO';
  license_number?: string;
  status: 'DRAFT' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'ISSUED';
  issue_date?: string;
  expiry_date?: string;
  created_at: string;
  updated_at: string;
}

export interface MockApplication {
  id: number;
  applicant_id: number;
  application_type: 'NEW' | 'RENEWAL';
  license_category: 'PILOT' | 'CABIN_CREW' | 'FLIGHT_DISPATCHER' | 'AME' | 'ATC' | 'ATSEP' | 'ASO';
  status: 'DRAFT' | 'SUBMITTED' | 'DOCUMENT_REVIEW' | 'BIOMETRIC_PENDING' | 'MEDICAL_REVIEW' | 'APPROVED' | 'REJECTED';
  current_step: number;
  total_steps: number;
  submitted_at?: string;
  created_at: string;
  updated_at: string;
  
  // Application data
  form_data: Record<string, any>;
  existing_license_number?: string; // For renewals
  
  // Progress tracking
  steps_completed: string[];
  rejection_reason?: string;
  reviewer_notes?: string;
}

export interface ApplicationStep {
  id: number;
  label: string;
  description: string;
  status: 'COMPLETED' | 'IN_PROGRESS' | 'PENDING' | 'REJECTED';
  completed_at?: string;
  icon: string;
}

// Use the constants from utils
export const LICENSE_TYPES = LICENSE_CATEGORY_LABELS;

export const mockLicenses: MockLicense[] = [
  {
    id: 1,
    applicant_id: 1,
    category: 'PILOT',
    license_number: 'NCAA-PILOT-2024-001234',
    status: 'ISSUED',
    issue_date: '2024-06-15',
    expiry_date: '2029-06-15',
    created_at: '2024-01-15T10:30:00Z',
    updated_at: '2024-06-15T14:22:00Z',
  },
  {
    id: 2,
    applicant_id: 2,
    category: 'ATC',
    license_number: 'NCAA-ATC-2023-005678',
    status: 'ISSUED',
    issue_date: '2023-08-20',
    expiry_date: '2028-08-20',
    created_at: '2023-05-10T09:00:00Z',
    updated_at: '2023-08-20T11:30:00Z',
  },
];

export const mockApplications: MockApplication[] = [
  {
    id: 1,
    applicant_id: 1,
    application_type: 'NEW',
    license_category: 'CABIN_CREW',
    status: 'DOCUMENT_REVIEW',
    current_step: 3,
    total_steps: 6,
    submitted_at: '2025-01-05T10:00:00Z',
    created_at: '2025-01-03T08:30:00Z',
    updated_at: '2025-01-08T15:45:00Z',
    form_data: {
      surname: 'Okafor',
      first_name: 'John',
      middle_name: 'Chukwuma',
      dob: '1995-03-15',
      medical_class: 'CLASS_2',
      height: 175,
      weight: 72,
    },
    steps_completed: ['APPLICATION_SUBMITTED', 'NIN_VERIFIED', 'DOCUMENTS_UPLOADED'],
    reviewer_notes: 'All documents received. Medical certificate under review.',
  },
  {
    id: 2,
    applicant_id: 2,
    application_type: 'RENEWAL',
    license_category: 'ATC',
    status: 'APPROVED',
    current_step: 6,
    total_steps: 6,
    submitted_at: '2025-01-02T09:00:00Z',
    created_at: '2024-12-28T14:00:00Z',
    updated_at: '2025-01-10T10:00:00Z',
    existing_license_number: 'NCAA-ATC-2023-005678',
    form_data: {
      license_number: 'NCAA-ATC-2023-005678',
      license_expiry_date: '2028-08-20',
      current_ratings: 'Tower Control, Approach Control',
      proficiency_valid_till: '2025-12-31',
      english_level: '5',
      english_valid_till: '2028-08-20',
    },
    steps_completed: ['APPLICATION_SUBMITTED', 'NIN_VERIFIED', 'DOCUMENTS_VERIFIED', 'BIOMETRIC_COMPLETED', 'MEDICAL_APPROVED', 'FINAL_APPROVED'],
    reviewer_notes: 'Renewal approved. License extended.',
  },
];

export const getApplicationSteps = (application: MockApplication): ApplicationStep[] => {
  const steps: ApplicationStep[] = [
    {
      id: 1,
      label: 'Application Submitted',
      description: 'Your application has been received',
      status: 'COMPLETED',
      completed_at: application.submitted_at,
      icon: 'FileText',
    },
    {
      id: 2,
      label: 'NIN Verification',
      description: 'National Identity Number verification',
      status: application.steps_completed.includes('NIN_VERIFIED') ? 'COMPLETED' : 'PENDING',
      completed_at: application.steps_completed.includes('NIN_VERIFIED') ? '2025-01-05T12:00:00Z' : undefined,
      icon: 'Shield',
    },
    {
      id: 3,
      label: 'Document Review',
      description: 'Required documents are being reviewed',
      status: application.status === 'DOCUMENT_REVIEW' ? 'IN_PROGRESS' : 
             application.steps_completed.includes('DOCUMENTS_VERIFIED') ? 'COMPLETED' : 'PENDING',
      icon: 'FileCheck',
    },
    {
      id: 4,
      label: 'Biometric Capture',
      description: 'Fingerprint and photo capture',
      status: application.status === 'BIOMETRIC_PENDING' ? 'IN_PROGRESS' :
             application.steps_completed.includes('BIOMETRIC_COMPLETED') ? 'COMPLETED' : 'PENDING',
      icon: 'Fingerprint',
    },
    {
      id: 5,
      label: 'Medical Review',
      description: 'Medical fitness assessment',
      status: application.status === 'MEDICAL_REVIEW' ? 'IN_PROGRESS' :
             application.steps_completed.includes('MEDICAL_APPROVED') ? 'COMPLETED' : 'PENDING',
      icon: 'Heart',
    },
    {
      id: 6,
      label: 'Final Approval',
      description: 'License issuance and activation',
      status: application.status === 'APPROVED' ? 'COMPLETED' : 'PENDING',
      completed_at: application.status === 'APPROVED' ? application.updated_at : undefined,
      icon: 'CheckCircle',
    },
  ];

  return steps;
};

// Helper to get current application for logged-in user
export const getCurrentApplication = (): MockApplication | null => {
  return mockApplications.find(app => app.applicant_id === 1) || null;
};

// Helper to get all licenses for logged-in user
export const getMyLicenses = (): MockLicense[] => {
  return mockLicenses.filter(lic => lic.applicant_id === 1);
};
